import React, { useMemo, useState } from 'react'
import { Header, Message, Input, Dropdown, Icon } from 'semantic-ui-react'
import { LoadingWrapper } from '../common'
import { SummaryCard, FilterPill } from '../common/DashboardWidgets'
import { AssetTeamPanel } from './AssetTeamPanel'
import { useAssetDashboard } from './useAssetDashboard'
import { ASSET_STATUS_FILTERS, teamMatchesAssetStatusFilter } from './assetStatusFilters'
import './AssetDashboard.css'

// ─── AssetDashboard ───────────────────────────────────────────────────────────

export const AssetDashboard = () => {
  const { teams, loading, error } = useAssetDashboard()
  const [search, setSearch] = useState('')
  const [marketFilter, setMarketFilter] = useState('')
  const [assetStatusFilter, setAssetStatusFilter] = useState('all')
  const [groupByMarket, setGroupByMarket] = useState(true)
  const [expandedTeamIds, setExpandedTeamIds] = useState(() => new Set())

  const toggleTeam = teamId => setExpandedTeamIds(prev => {
    const next = new Set(prev)
    if (next.has(teamId)) next.delete(teamId)
    else next.add(teamId)
    return next
  })

  // ── Market dropdown options ───────────────────────────────────────────────
  const marketOptions = useMemo(() => {
    const markets = [...new Map(
      teams.filter(t => t.market).map(t => [t.market.id, t.market])
    ).values()].sort((a, b) => a.label.localeCompare(b.label))
    return [
      { key: '', value: '', text: 'All Markets' },
      ...markets.map(m => ({ key: m.id, value: m.id, text: m.label })),
    ]
  }, [teams])

  // ── Overall totals (computed client-side from per-team summaries) ────────
  const overall = useMemo(() => teams.reduce((acc, t) => {
    const s = t.summary ?? {}
    acc.assetsTotal += s.assetsTotal ?? 0
    acc.assetsFunctional += s.assetsFunctional ?? 0
    acc.openProblems += s.openProblemsCount ?? 0
    acc.openStanddowns += s.openStanddownsCount ?? 0
    return acc
  }, { assetsTotal: 0, assetsFunctional: 0, openProblems: 0, openStanddowns: 0 }), [teams])

  // ── Filtered teams (active teams only) ────────────────────────────────────
  const filteredTeams = useMemo(() => teams.filter(t => {
    if (!t.isActive) return false
    if (search && !t.name.toLowerCase().includes(search.toLowerCase())) return false
    if (marketFilter && t.market?.id !== marketFilter) return false
    if (!teamMatchesAssetStatusFilter(t, assetStatusFilter)) return false
    return true
  }), [teams, search, marketFilter, assetStatusFilter])

  // ── Grouped by market (client-side only — Scenario: Group by Market) ─────
  const grouped = useMemo(() => {
    if (!groupByMarket) return null
    const groups = {}
    filteredTeams.forEach(t => {
      const key = t.market ? `${t.market.id}` : 'unknown'
      if (!groups[key]) groups[key] = { label: t.market?.label ?? 'Unknown', teams: [] }
      groups[key].teams.push(t)
    })
    return Object.values(groups).sort((a, b) => a.label.localeCompare(b.label))
  }, [filteredTeams, groupByMarket])

  const allExpanded = filteredTeams.length > 0 && filteredTeams.every(t => expandedTeamIds.has(t.id))
  const toggleExpandAll = () => setExpandedTeamIds(allExpanded ? new Set() : new Set(filteredTeams.map(t => t.id)))

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="mgr-dashboard">
      <Header as="h2">Asset Dashboard</Header>
      <LoadingWrapper loading={loading}>
        {error && (
          <Message negative>
            <Message.Header>Error loading dashboard</Message.Header>
            <p>{error}</p>
          </Message>
        )}

        {!error && (
          <>
            {/* ── Summary cards ── */}
            <div className="mgr-summary-row">
              <SummaryCard
                label="Asset Health"
                value={`${overall.assetsFunctional}/${overall.assetsTotal}`}
                sub="functional"
                icon="truck"
                color={overall.assetsFunctional < overall.assetsTotal ? '#f57f17' : '#43a047'}
                alert={overall.assetsFunctional < overall.assetsTotal}
              />
              <SummaryCard
                label="Open Problems"
                value={overall.openProblems}
                sub="asset issues"
                icon="wrench"
                color={overall.openProblems > 0 ? '#e65100' : undefined}
                alert={overall.openProblems > 0}
              />
              <SummaryCard
                label="Open Stand-downs"
                value={overall.openStanddowns}
                sub="assets stood down"
                icon="tags"
                color={overall.openStanddowns > 0 ? '#e53935' : undefined}
                alert={overall.openStanddowns > 0}
              />
              <SummaryCard
                label="Teams"
                value={teams.length}
                sub="visible teams"
                icon="users"
              />
            </div>

            {/* ── Filter bar ── */}
            <div className="mgr-filter-bar">
              {/* Row 1: search + market + group-by */}
              <div className="mgr-filter-bar__top">
                <Input
                  icon="search"
                  placeholder="Search teams..."
                  value={search}
                  onChange={(_, { value }) => setSearch(value)}
                  size="large"
                />
                <Dropdown
                  selection
                  search
                  options={marketOptions}
                  value={marketFilter}
                  onChange={(_, { value }) => setMarketFilter(value)}
                  placeholder="All Markets"
                  style={{ minWidth: '150px' }}
                />
                <div style={{ marginLeft: 'auto', display: 'flex', gap: '0.5rem' }}>
                  <FilterPill
                    label={allExpanded ? 'Collapse All' : 'Expand All'}
                    onClick={toggleExpandAll}
                  />
                  <FilterPill
                    label={groupByMarket ? '✓ Grouped by Market' : 'Group by Market'}
                    active={groupByMarket}
                    onClick={() => setGroupByMarket(g => !g)}
                  />
                </div>
              </div>

              {/* Row 2: quick filter pills */}
              <div className="mgr-filter-bar__pills-row">
                <div className="mgr-filter-group">
                  <span className="mgr-filter-label">Asset Status:</span>
                  {ASSET_STATUS_FILTERS.map(f => (
                    <FilterPill key={f.key} label={f.label} active={assetStatusFilter === f.key} onClick={() => setAssetStatusFilter(f.key)} />
                  ))}
                </div>
              </div>
            </div>

            {/* ── Results count ── */}
            <div className="mgr-results-count">
              Showing {filteredTeams.length} of {teams.length} team{teams.length !== 1 ? 's' : ''}
            </div>

            {/* ── Team list ── */}
            {teams.length === 0 ? (
              <Message info>
                <Message.Header>No teams found</Message.Header>
                <p>There are no teams with visible assets for your account.</p>
              </Message>
            ) : filteredTeams.length === 0 ? (
              <Message info>
                <Message.Header>No teams match the current filters</Message.Header>
                <p>Try adjusting your search or filter criteria.</p>
              </Message>
            ) : grouped ? (
              grouped.map(group => (
                <div key={group.label} className="mgr-market-group">
                  <div className="mgr-market-group__header">
                    <Icon name="map marker alternate" color="grey" />
                    {group.label} ({group.teams.length})
                  </div>
                  {group.teams.map(team => (
                    <AssetTeamPanel
                      key={team.id}
                      team={team}
                      assetStatusFilter={assetStatusFilter}
                      expanded={expandedTeamIds.has(team.id)}
                      onToggleExpand={toggleTeam}
                    />
                  ))}
                </div>
              ))
            ) : (
              filteredTeams.map(team => (
                <AssetTeamPanel
                  key={team.id}
                  team={team}
                  assetStatusFilter={assetStatusFilter}
                  expanded={expandedTeamIds.has(team.id)}
                  onToggleExpand={toggleTeam}
                />
              ))
            )}
          </>
        )}
      </LoadingWrapper>
    </div>
  )
}
